import React, { useEffect, useRef, useState } from 'react'
import { Container, Row, Col, Card, Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import Select from "react-select"
import Swal from 'sweetalert2'
import Loader from '../../components/loaders/Loader'
import { addMultipleDocuments, getDocuments, deleteMultiple } from '../../actions/other/documentsActions'
import { AppDispatch, RootState } from '../../store'
import { CustomDataTable } from '../../components/design/Datatables/CustomDataTable'
import { getCategories } from '../../actions/others/categoriesActions'
import { getSucursals } from '../../actions/other/sucursalsActions'

export interface Document {
    _id?: string
    title?: string
    description?: string
    files?: (File | DocumentFile)[]
    date?: string | Date
    roles?: string[]
    categories?: Category[]
    sucursals?: Sucursal[]
}

export interface DocumentFile {
    _id?: string
    name?: string
    url?: string
}

export interface Category {
    _id: string
    name: string
}

export interface Sucursal {
    _id: string
    descripcion: string
}

export default function UploadDocuments() {
    const { t } = useTranslation()
    const dispatch: AppDispatch = useDispatch()

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(25)
    const [search, setSearch] = useState<string>("")
    const [searchAux, setSearchAux] = useState('')
    const [totalRows, setTotalRows] = useState<number>(0)

    const { documents, loading, resultdocument } = useSelector((state: RootState) => state.documents)
    const { categories } = useSelector((state: RootState) => state.categories)
    const { sucursals } = useSelector((state: RootState) => state.sucursals)

    const [formState, setFormState] = useState<Document>({})
    const fileInput = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        dispatch(getDocuments(1, 999999, search) as any)
        dispatch(getCategories(1, 999999, search) as any)
        dispatch(getSucursals() as any)
    }, [search])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!formState.title || !formState.files || formState.files.length === 0) {
            Swal.fire({
                showConfirmButton: true,
                showDenyButton: false,
                confirmButtonText: t('global.ok'),
                icon: 'error',
                text: t('documentTranslate.documentSubmitError')
            })
        } else {
            const payload = {
                ...formState,
                categories: formState.categories?.map(c => c._id),
                sucursals: formState.sucursals?.map(c => c._id)
            }

            dispatch(addMultipleDocuments({ type: 'documents', ...payload }))
        }
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()

        const files = Array.from(e.dataTransfer.files)
        setFormState(prev => ({ ...prev, files }))
    }

    const handleFiles = (files: File[]) => {
        setFormState(prev => ({ ...prev, files }))
    }

    const handleDelete = (documentIds: string[]) => {
        Swal.fire({
            title: t('documentTranslate.deleteDocumentsQuestion'),
            showDenyButton: true,
            confirmButtonText: t('global.ok'),
            denyButtonText: t('global.cancel'),
            icon: 'warning'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteMultiple(documentIds) as any)
            }
        })
    }

    useEffect(() => {
        if (resultdocument) {
            let resultnow = resultdocument
            dispatch({ type: 'DOCUMENT_RESET_RESULT' })
            switch (resultnow) {
                case 'addMultipleSuccess':
                    if (
                        typeof resultdocument !== 'string' &&
                        resultdocument?.rejectedFiles &&
                        resultdocument.rejectedFiles.length > 0
                    ) {
                        Swal.fire({
                            showConfirmButton: true,
                            showDenyButton: false,
                            confirmButtonText: t('global.ok'),
                            icon: 'warning',
                            text: t('documentTranslate.addMultipleSuccessWithIssues', {
                                rejectedFiles: resultdocument.rejectedFiles.join(', ')
                            })
                        })
                    } else {
                        Swal.fire({
                            showConfirmButton: true,
                            icon: 'success',
                            title: t('documentTranslate.addMultipleSuccess'),
                        })
                        setFormState({})

                        if (fileInput.current) {
                            fileInput.current.value = ''
                        }
                    }
                    break;
                default:
                    break;
            }
        }
        // eslint-disable-next-line
    }, [resultdocument, dispatch, t])

    const columnsDocuments = [
        {
            name: t('documentTranslate.date'),
            selector: (document: Document) => document.date ? new Date(document.date) : new Date(0),
            cell: (document: Document) => document.date ? new Date(document.date).toLocaleDateString() : '-',
            sortable: true,
        },
        {
            name: t('documentTranslate.title'),
            selector: (document: Document) => document.title || document.description,
            cell: (document: Document) => document.title || document.description,
            sortable: true,
        },
        {
            name: t('documentTranslate.roles'),
            selector: (document: Document) => document.roles,
            cell: (document: Document) => document.roles?.map(role => t(role)).join(', '),
            sortable: true,
        },
        {
            name: t('documentTranslate.sucursals'),
            selector: (document: Document) => document.sucursals,
            cell: (document: Document) => document.sucursals?.map(sucursal => sucursal?.descripcion).join(', '),
            sortable: true,
        },
        {
            name: t('documentTranslate.categories'),
            selector: (document: Document) => document.categories,
            cell: (document: Document) => document.categories?.map(category => category?.name).join(', '),
            sortable: true,
        },
        {
            name: t('documentTranslate.documents'),
            cell: (document: Document) => (
                <>
                    {document.files && document.files.length > 0 ? (
                        <ul className="list-unstyled mb-0 text-start">
                            {document.files.map((file, index) => {
                                if ("url" in file) {
                                    return (
                                        <li key={(file as DocumentFile)._id || index}>
                                            <a
                                                href={(file as DocumentFile).url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-dark"
                                            >
                                                {(file as DocumentFile).name}
                                            </a>
                                        </li>
                                    )
                                }

                                return (
                                    <li key={index}>
                                        <a
                                            href={URL.createObjectURL(file as File)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-dark"
                                        >
                                            {(file as File).name}
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    ) : (
                        <span>-</span>
                    )}
                </>
            ),
            sortable: false,
        },
        {
            name: t('global.actions'),
            cell: (document: Document) => (
                <i className='fas fa-trash cursor' onClick={() => handleDelete([document._id!])}></i>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ]

    return (
        <Container className='contenido'>
            <Row>
                <Col className='mt-3 px-1'>
                    <Row>
                        <Col xs={12} >
                            <h1>{t('menus.documentsManagement')}</h1>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} className='my-3 px-1'>
                            <Card>
                                <Card.Body className='p-5'>
                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            <Col xs={12} className='my-3'>
                                                <Form.Label>{t("documentTranslate.title")} ({t('global.mandatory')})</Form.Label>
                                                <Form.Control
                                                    type='text'
                                                    name='title'
                                                    value={formState.title || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </Col>
                                            <Col xs={12} className='my-3'>
                                                <Form.Label>{t("documentTranslate.description")}</Form.Label>
                                                <Form.Control
                                                    type='text'
                                                    as='textarea'
                                                    name='description'
                                                    value={formState.description || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </Col>
                                            <Col xs={4} className='my-3'>
                                                <Form.Label>{t("documentTranslate.sucursals")}</Form.Label>
                                                <Select
                                                    isMulti
                                                    name="sucursals"
                                                    options={sucursals?.map(sucursal => ({
                                                        value: sucursal._id,
                                                        label: sucursal.descripcion
                                                    }))}

                                                    value={formState.sucursals?.map(cat => ({
                                                        value: cat._id,
                                                        label: cat.descripcion
                                                    })) ?? []}

                                                    onChange={(selected) =>
                                                        setFormState(prev => ({
                                                            ...prev,
                                                            sucursals: selected.map(s => ({
                                                                _id: s.value,
                                                                descripcion: s.label
                                                            }))
                                                        }))
                                                    }
                                                />
                                            </Col>
                                            <Col xs={4} className='my-3'>
                                                <Form.Label>{t("documentTranslate.roles")}</Form.Label>
                                                <Select
                                                    isMulti
                                                    name="roles"
                                                    options={[
                                                        { value: "commercial", label: t("commercial") },
                                                        { value: "clerk", label: t("clerk") },
                                                        { value: "adminClerk", label: t("adminClerk") },
                                                    ]}
                                                    value={formState.roles?.map(rol => ({
                                                        value: rol,
                                                        label: t(rol)
                                                    })) || []}
                                                    onChange={(selected) =>
                                                        setFormState(prev => ({
                                                            ...prev,
                                                            roles: selected.map((s) => s.value)
                                                        }))
                                                    }
                                                />
                                            </Col>
                                            <Col xs={4} className='my-3'>
                                                <Form.Label>{t("documentTranslate.categories")}</Form.Label>
                                                <Select
                                                    isMulti
                                                    name="categories"
                                                    options={categories?.map(category => ({
                                                        value: category._id,
                                                        label: category.name
                                                    }))}

                                                    value={formState.categories?.map(cat => ({
                                                        value: cat._id,
                                                        label: cat.name
                                                    })) ?? []}

                                                    onChange={(selected) =>
                                                        setFormState(prev => ({
                                                            ...prev,
                                                            categories: selected.map(s => ({
                                                                _id: s.value,
                                                                name: s.label
                                                            }))
                                                        }))
                                                    }
                                                />
                                            </Col>
                                            <Col xs={12} className='my-3'>
                                                <Form.Label>{t("documentTranslate.documents")} ({t('global.mandatory')})</Form.Label>
                                                <div
                                                    className='draganddrop cursor'
                                                    onDrop={handleDrop}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onClick={() => fileInput.current?.click()}
                                                >
                                                    <div className='mb-2 text-center'>
                                                        <p>
                                                            {t('global.draganddrop')}
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>{t('documentTranslate.dniDocumentsDescription')}</Tooltip>}
                                                            >
                                                                <i className='fas fa-info-circle ms-1'></i>
                                                            </OverlayTrigger>
                                                        </p>

                                                        <i className='fas fa-cloud-arrow-up fa-3x'></i>
                                                    </div>

                                                    <input
                                                        type="file"
                                                        multiple
                                                        ref={fileInput}
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => e.target.files && handleFiles(Array.from(e.target.files))}
                                                    />
                                                    {
                                                        formState.files && formState.files.length > 0 &&
                                                        <div className='fw-bold'>{formState.files.map(f => f.name).join(', ')}</div>
                                                    }
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={12} md={6} className='my-3 ms-auto text-end'>
                                                <Button type="submit" variant="primary" className='width-300'>
                                                    {t("documentTranslate.upload")}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className='mt-3 mb-5'>
                        <Col className='px-1'>
                            <Card>
                                <Card.Body>
                                    <Row className="justify-content-end">
                                        <Col xs={12} lg={4} className="d-flex">
                                            <input
                                                type="text"
                                                className="form-control me-2 input-search"
                                                placeholder={t("datatables.buscar")}
                                                value={searchAux || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setSearchAux(e.target.value);
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch(searchAux);
                                                        setPage(1);
                                                    }
                                                }}
                                            />
                                            <Button
                                                className="button-search"
                                                onClick={() => setSearch(searchAux)}
                                            >
                                                <i className="fas fa-search" />
                                            </Button>
                                        </Col>
                                    </Row>
                                    {loading ?
                                        <Loader />
                                        :
                                        documents?.length > 0
                                            ? <>
                                                <div className='d-md-block d-none'>
                                                    <CustomDataTable
                                                        pagination
                                                        paginationServer
                                                        columns={columnsDocuments}
                                                        data={documents}
                                                        exportable={false}
                                                        printable={false}
                                                        searchText={search}
                                                        onChangeRowsPerPage={(newLimit: number) => {
                                                            setLimit(newLimit);
                                                            setPage(1);
                                                        }}
                                                        onChangePage={(newPage: number) => {
                                                            setPage(newPage);
                                                        }}
                                                    />
                                                </div>
                                            </>
                                            : <p className='mt-4 text-center'>{t('documentTranslate.noDocuments')}</p>
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Container>
    )
}